import React, { useState } from 'react';
import {
  Layout,
  Card,
  Steps,
  Button,
  Space,
  Form,
  Input,
  Select,
  App,
  Typography,
  List,
  Empty,
  Divider,
  Row,
  Col,
  Tag,
  Alert,
  Modal
} from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  EditOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MessageOutlined,
  SendOutlined,
  AudioOutlined,
  FormOutlined,
  ThunderboltOutlined,
  StopOutlined,
  BarChartOutlined,
  FolderOpenOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useReportWriter } from '../../context/ReportWriterContext';
import reportAiService from '../../services/reportAiService';
import logger from '../../utils/logger';
import pptxgen from 'pptxgenjs';
import jsPDF from 'jspdf';
import api from '../../services/api';
import { generatePowerPointBlob as generatePowerPointBlobUtil, generateExportFilename } from '../../utils/pptxGenerator';
import RichTextSection from './RichTextSection';
import ExampleModal from './components/common/ExampleModal';
import StepProgress from './components/common/StepProgress';
import ReportTypeSelection from './components/steps/ReportTypeSelection';
import InputModeSelection from './components/steps/InputModeSelection';
import CompactFormView from './components/forms/CompactFormView';
import VoiceModeView from './components/forms/VoiceModeView';
import ReportFormFilling from './components/steps/ReportFormFilling';
import ReportActions from './components/steps/ReportActions';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Step } = Steps;
const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8080';

const ReportWriterV2 = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // Get context
  const {
    formData,
    sessionId,
    clearReport,
    selectedReport,
    chatMessages,
    setChatMessages,
    addChatMessage,
    updateFormData,
    setSelectedReport
  } = useReportWriter();

  // Local state
  const [currentStep, setCurrentStep] = useState(0);
  const [inputMode, setInputMode] = useState(null); // 'direct' or 'voice'
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [currentSection, setCurrentSection] = useState('');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [sectionTranscripts, setSectionTranscripts] = useState({}); // Store transcripts per section
  const [exampleModalOpen, setExampleModalOpen] = useState(false);
  const [examplePdfUrl, setExamplePdfUrl] = useState('');

  // AI Assistant state (now embedded in workspace)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [splitRatio, setSplitRatio] = useState(60); // 60% form, 40% assistant
  const [isDragging, setIsDragging] = useState(false);
  const [activeOperation, setActiveOperation] = useState(null); // 'evaluate' or 'archive'
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  // Form sections with numbering, correct order, and support text
  const kgSections = [
    {
      key: 'title',
      label: 'Knowledge Gap Title',
      number: null,
      isTextOnly: true,
      supportText: null
    },
    {
      key: 'owner',
      label: 'Owner',
      number: null,
      isTextOnly: true,
      supportText: null
    },
    {
      key: 'project_name',
      label: 'Project Name',
      number: null,
      isTextOnly: true,
      supportText: null
    },
    {
      key: 'description',
      label: 'The Question to Answer',
      number: 1,
      charLimit: 125,
      supportText: 'Is our Question to Answer:\n• Relevant: The answer will help us make a better Key Decision.\n• Answerable: We can get an answer in a realistic amount of time.\n• Unbiased: Does not pre-suppose an answer nor imply a solution.\n• Focused: We will be able to tell when we have answered the question.'
    },
    {
      key: 'purpose',
      label: 'The Purpose',
      number: 2,
      charLimit: 300,
      supportText: 'Does the Purpose clearly explain:\n• What do we want?\n• What is in our way?\n• What could we do to overcome that obstacle? OR What is our idea?\n• What do we need to know?'
    },
    {
      key: 'what_we_have_done',
      label: 'What We Will Do / Have Done',
      number: 3,
      charLimit: 850,
      supportText: 'Consider the Learning Activities:\n• Are these Learning Activities appropriate for this type of Knowledge Gap?\n• How will we know when we are done?\n• Is there a faster or easier way to find out what we need to know?\n\nDoes the summary:\n• Give me a rough idea of what the team did?\n• Make me feel confident they didn\'t miss anything important?\n• Provide links or references if I want to know more?'
    },
    {
      key: 'what_we_have_learned',
      label: 'What We Have Learned',
      number: 4,
      charLimit: 1200,
      supportText: 'Does this section:\n• Clearly state the key learnings?\n• Explain how the learnings are relevant to the Key Decision?\n• Employ visual models rather than text when possible?'
    },
    {
      key: 'recommendations',
      label: 'Recommendations and Next Steps',
      number: 5,
      charLimit: 300,
      supportText: '• Have you stated whether this Knowledge Gap should be closed now, continued in the next Learning Cycle, or shelved?\n• Are you recommending any new Knowledge Gaps or additional investigation? If yes, why? (Hint: if there is still significant risk).\n• Is there anything else the team should do next?'
    }
  ];

  const kdSections = [
    { key: 'title', label: 'Key Decision Title', number: null, isTextOnly: true, supportText: null },
    { key: 'owner', label: 'Owner', number: null, isTextOnly: true, supportText: null },
    { key: 'project_name', label: 'Project Name', number: null, isTextOnly: true, supportText: null },
    {
      key: 'description',
      label: 'The Key Decision',
      number: 1,
      charLimit: 125,
      supportText: 'Is the Key Decision:\n• Significant: The consequences of a wrong decision are serious.\n• Actionable: The decision clearly drives subsequent actions.\n• Unbiased: Does not pre-suppose an answer or imply a decision.\n• Focused: We can tell what will be affected by the decision.'
    },
    {
      key: 'purpose',
      label: 'The Purpose',
      number: 2,
      charLimit: 300,
      supportText: 'Does the Purpose clearly explain:\n• What do we want?\n• What are we deciding and when do we need to know?\n• What makes this decision high impact?\n• What makes this decision high unknown?'
    },
    {
      key: 'what_we_have_done',
      label: 'What We Have Done',
      number: 3,
      charLimit: 850,
      supportText: 'Does the summary of the Knowledge Gaps in What We Have Done:\n• Give me a rough idea of what the team did and didn\'t do?\n• Make me feel confident they didn\'t miss anything important?\n• Provide links or references to the Knowledge Gap Reports?'
    },
    {
      key: 'what_we_have_learned',
      label: 'What We Have Learned',
      number: 4,
      charLimit: 900,
      supportText: 'In What We Have Learned:\n• Do I understand the key learnings?\n• Is there a clear recommendation for the Key Decision?\n• Is the rationale for that recommendation clear and complete?\n• Are your learnings clearly stated?\n• Are the pros and cons of the options clear? (Hint: try a visual model)\n• If using a decision table, did you avoid adding up scores? (unless all criteria could be measured the same way)\n• Is the rationale for the final conclusion stated?'
    },
    {
      key: 'recommendations',
      label: 'What We Recommend / What We Have Decided',
      number: 5,
      charLimit: 600,
      supportText: 'Recommendations and Next Steps:\n• Have you stated your recommendation clearly?\n• Does the recommendation clearly flow from the conclusion?\n• Have you summarized remaining risks or unknowns?\n• Is there anything else the team should do next?\n\nIn the What We Have Decided section:\n• Is the final decision clearly stated?\n• Does the Learning Plan need to be updated in any way?\n• Note: if the Key Decision is later revisited, this Key Decision report should be updated accordingly.'
    }
  ];

  const sections = selectedReport === 'knowledge_gap' ? kgSections : kdSections;

  // Get only numbered sections for voice mode (exclude text-only fields like title, owner, project name)
  const voiceSections = sections.filter(s => s.number !== null);

  // Validation for required fields
  const validateRequiredFields = () => {
    const requiredFields = ['title', 'owner', 'contributors', 'project_name'];

    if (selectedReport === 'knowledge_gap') {
      requiredFields.push('kg_number', 'status');
    } else {
      requiredFields.push('kd_number', 'status');
    }

    const missingFields = [];
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        missingFields.push(field);
      }
    });

    if (missingFields.length > 0) {
      const fieldLabels = missingFields.map(f => {
        if (f === 'kg_number') return 'KG #';
        if (f === 'kd_number') return 'KD #';
        return f.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      });
      message.error(`Please fill in required fields: ${fieldLabels.join(', ')}`);
      return false;
    }
    return true;
  };

  // Handle report type selection
  const handleReportTypeSelect = (value) => {
    setSelectedReport(value);
    setCurrentStep(1); // Move to input mode selection
    message.success(`${value === 'knowledge_gap' ? 'Knowledge Gap' : 'Key Decision'} report selected`);
  };

  // Handle input mode selection
  const handleInputModeSelect = (mode) => {
    setInputMode(mode);
    setCurrentStep(2); // Move to form filling
    if (mode === 'voice') {
      const voiceSections = sections.filter(s => s.number !== null);
      setCurrentSection(voiceSections[0]?.key);
      message.info('Click the microphone button to start voice input');
    } else if (mode === 'assistant') {
      // Set first section for assistant mode too
      const voiceSections = sections.filter(s => s.number !== null);
      setCurrentSection(voiceSections[0]?.key);
      // Open assistant panel for assistant mode
      setDrawerOpen(true);
    }
    // Direct mode: drawer stays closed by default
  };

  // Voice input with Web Speech API
  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      message.error("Voice recognition not supported in your browser. Use Chrome or Edge.");
      return;
    }

    if (isRecording) {
      // Stop recording
      window.speechRecognitionInstance?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    window.speechRecognitionInstance = recognition;

    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    setIsRecording(true);
    let finalTranscript = '';

    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      setVoiceTranscript(finalTranscript + interimTranscript);
    };

    recognition.onerror = (event) => {
      logger.error('Speech recognition error', event.error);
      setIsRecording(false);

      if (event.error === 'not-allowed') {
        message.error('Microphone access denied');
      } else {
        message.error(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsRecording(false);

      if (finalTranscript.trim()) {
        // Send to AI for formatting
        handleVoiceToAI(finalTranscript.trim());
      }
    };

    try {
      recognition.start();
      message.info('Listening... Speak now');
    } catch (err) {
      logger.error('Error starting speech recognition:', err);
      setIsRecording(false);
    }
  };

  // Send voice transcript to AI for formatting
  const handleVoiceToAI = async (transcript) => {
    const currentSectionData = sections.find(s => s.key === currentSection);

    // Save raw transcript
    setSectionTranscripts(prev => ({
      ...prev,
      [currentSection]: {
        raw: transcript,
        formatted: null,
        loading: true
      }
    }));

    setLoading(true);

    try {
      const prompt = `The user is filling out the "${currentSectionData.label}" section of a ${selectedReport === 'knowledge_gap' ? 'Knowledge Gap' : 'Key Decision'} report. They said: "${transcript}". Please convert this to a professional, well-structured response suitable for this section. Be concise and formal.`;

      let response;
      if (selectedReport === 'knowledge_gap') {
        response = await reportAiService.processKGMessage(prompt, null, formData, sessionId);
      } else {
        response = await reportAiService.processKDMessage(prompt, null, formData, sessionId);
      }

      const formattedContent = response.answer || transcript;

      // Save formatted content
      setSectionTranscripts(prev => ({
        ...prev,
        [currentSection]: {
          raw: transcript,
          formatted: formattedContent,
          loading: false
        }
      }));

      // Update form with formatted content
      updateFormData({ [currentSection]: formattedContent });

      setVoiceTranscript('');
      message.success(`Section "${currentSectionData.label}" processed`);

      // Don't auto-advance - let user select next section
    } catch (err) {
      logger.error('Error processing voice input:', err);
      setSectionTranscripts(prev => ({
        ...prev,
        [currentSection]: {
          raw: transcript,
          formatted: null,
          loading: false,
          error: true
        }
      }));
      message.error('Failed to process voice input');
    } finally {
      setLoading(false);
    }
  };

  // Handle chat submission (for direct AI assistance)
  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput;
    addChatMessage({ role: 'user', content: userMessage });
    setChatInput('');
    setLoading(true);

    try {
      const reportContext = formData;
      let response;

      if (selectedReport === 'knowledge_gap') {
        response = await reportAiService.processKGMessage(userMessage, null, reportContext, sessionId);
      } else {
        response = await reportAiService.processKDMessage(userMessage, null, reportContext, sessionId);
      }

      addChatMessage({
        role: 'ai',
        content: response.answer || "Sorry, I couldn't process your request."
      });
    } catch (err) {
      logger.error('Error from AI:', err);
      addChatMessage({
        role: 'ai',
        content: err.message || "Sorry, there was an error."
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate PowerPoint as Blob (for PDF conversion pipeline)
  // Now uses utility module for better testability
  const generatePowerPointBlob = async (slideSize = '16:9') => {
    return await generatePowerPointBlobUtil(formData, selectedReport, slideSize);
  };

  // Shared helper functions for HTML parsing (used by both PowerPoint and PDF exports)

  // Helper function to extract tables from HTML
  const extractTablesFromHTML = (html) => {
    if (!html) return [];

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const tables = [];

      doc.querySelectorAll('table').forEach(table => {
        const rows = [];

        table.querySelectorAll('tr').forEach(tr => {
          const cells = [];
          tr.querySelectorAll('td, th').forEach(cell => {
            cells.push({
              text: cell.textContent.trim(),
              options: {
                bold: cell.tagName === 'TH',
                fontSize: 9,
                valign: 'middle',
                align: 'left'
              }
            });
          });

          if (cells.length > 0) {
            rows.push(cells);
          }
        });

        if (rows.length > 0) {
          tables.push({
            rows,
            colCount: Math.max(...rows.map(r => r.length))
          });
        }
      });

      return tables;
    } catch (error) {
      console.error('Error extracting tables:', error);
      return [];
    }
  };

  // Helper function to extract images from HTML and load their dimensions
  const extractImagesFromHTML = async (html) => {
    if (!html) return [];

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const imagePromises = [];

      doc.querySelectorAll('img').forEach(img => {
        if (img.src) {
          // Create a promise that loads the image and gets real dimensions
          const imagePromise = new Promise((resolve) => {
            const tempImg = new Image();
            tempImg.onload = () => {
              resolve({
                data: img.src,
                naturalWidth: tempImg.width,
                naturalHeight: tempImg.height,
                alt: img.alt || 'Image'
              });
            };
            tempImg.onerror = () => {
              // If image fails to load, use fallback
              resolve({
                data: img.src,
                naturalWidth: null,
                naturalHeight: null,
                alt: img.alt || 'Image'
              });
            };
            tempImg.src = img.src;
          });
          imagePromises.push(imagePromise);
        }
      });

      return await Promise.all(imagePromises);
    } catch (error) {
      console.error('Error extracting images:', error);
      return [];
    }
  };

  // Helper function to parse HTML and extract text with link information
  const parseHTMLWithLinks = (html) => {
    if (!html) return { segments: [], plainText: '' };

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Remove tables and images first (we'll handle them separately)
      doc.querySelectorAll('table, img').forEach(el => el.remove());

      const segments = [];
      let plainText = '';

      // Recursively extract text and links
      const extractTextSegments = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text.trim()) {
            segments.push({ text, isLink: false });
            plainText += text;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();

          if (tagName === 'a') {
            const text = node.textContent.trim();
            const href = node.getAttribute('href');
            if (text && href) {
              segments.push({ text, isLink: true, url: href });
              plainText += text;
            }
          } else if (tagName === 'br') {
            segments.push({ text: '\n', isLink: false });
            plainText += '\n';
          } else if (['p', 'div'].includes(tagName)) {
            node.childNodes.forEach(extractTextSegments);
            segments.push({ text: '\n', isLink: false });
            plainText += '\n';
          } else if (tagName === 'li') {
            segments.push({ text: '• ', isLink: false });
            plainText += '• ';
            node.childNodes.forEach(extractTextSegments);
            segments.push({ text: '\n', isLink: false });
            plainText += '\n';
          } else {
            node.childNodes.forEach(extractTextSegments);
          }
        }
      };

      doc.body.childNodes.forEach(extractTextSegments);

      return {
        segments: segments.filter(s => s.text),
        plainText: plainText.trim()
      };
    } catch (error) {
      console.error('Error parsing HTML with links:', error);
      // Fallback to simple text extraction
      const plainText = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .trim();
      return {
        segments: [{ text: plainText, isLink: false }],
        plainText
      };
    }
  };

  // Helper function to strip HTML and get plain text (for text without tables/images)
  const stripHTMLToText = (html) => {
    const parsed = parseHTMLWithLinks(html);
    return parsed.plainText;
  };

  // Export PowerPoint using template layout
  const exportPowerPoint = async (slideSize = '16:9') => {
    if (!selectedReport) {
      message.warning('Please select a report type first.');
      return;
    }

    message.loading('Generating PowerPoint with images...', 0);

    try {
      const pptx = new pptxgen();
      const fields = formData;

    // Set layout based on slideSize parameter
    pptx.layout = slideSize === '16:9' ? 'LAYOUT_16x9' : 'LAYOUT_4x3';

    // Color scheme based on report type
    const isKnowledgeGap = selectedReport === 'knowledge_gap';
    const headerBg = isKnowledgeGap ? 'FFFF66' : 'CCFF66';  // Yellow for KG, light green for KD
    const borderColor = '555555';  // Gray border from template

    // Expand horizontal space - use more of the slide width
    // Original template margins: 0.66" left, ~0.5" right
    // New margins: 0.3" left/right for maximum content area
    const leftMargin = 0.3;
    const rightMargin = 0.3;
    const slideWidth = 10;
    // Adjust slideHeight based on aspect ratio
    const slideHeight = slideSize === '16:9' ? 5.625 : 7.5;
    const availableWidth = slideWidth - leftMargin - rightMargin;
    const leftBoxWidth = availableWidth * 0.465;  // ~45% for left boxes
    const rightBoxWidth = availableWidth * 0.525;  // ~52.5% for right boxes
    const gap = availableWidth * 0.01;  // 1% gap between columns

    // Helper function to add content with tables and images to a box
    const addContentToBox = async (html, x, y, w, h, titleText) => {
      if (!html || html.trim() === '') {
        // Just add title if no content
        slide.addText(titleText, {
          x, y, w, h,
          fontSize: 10,
          fontFace: 'Arial',
          bold: true,
          fill: { color: 'FFFFFF' },
          line: { color: borderColor, width: 1 },
          shape: pptx.ShapeType.roundRect,
          rectRadius: cornerRadius,
          valign: 'top',
          margin: [0.08, 0.08, 0.08, 0.08]
        });
        return;
      }

      // Extract tables and images (images need to be loaded to get dimensions)
      const tables = extractTablesFromHTML(html);
      const images = await extractImagesFromHTML(html);
      const parsed = parseHTMLWithLinks(html);

      // If no tables or images, use simple text box with links
      if (tables.length === 0 && images.length === 0) {
        // Build text array with title and content segments (including links)
        const textArray = [{ text: titleText, options: { bold: true } }];

        // Add text segments with hyperlinks where applicable
        parsed.segments.forEach(segment => {
          if (segment.isLink) {
            textArray.push({
              text: segment.text,
              options: {
                hyperlink: { url: segment.url, tooltip: segment.url },
                color: '0563C1',
                underline: true
              }
            });
          } else {
            textArray.push({ text: segment.text });
          }
        });

        slide.addText(textArray, {
          x, y, w, h,
          fontSize: 10,
          fontFace: 'Arial',
          fill: { color: 'FFFFFF' },
          line: { color: borderColor, width: 1 },
          shape: pptx.ShapeType.roundRect,
          rectRadius: cornerRadius,
          valign: 'top',
          margin: [0.08, 0.08, 0.08, 0.08]
        });
        return;
      }

      // Complex content: Draw border box, then add text, tables, and images
      const padding = 0.08;

      // Add border box
      slide.addShape(pptx.ShapeType.roundRect, {
        x, y, w, h,
        fill: { color: 'FFFFFF' },
        line: { color: borderColor, width: 1 },
        rectRadius: cornerRadius
      });

      let currentY = y + padding;
      const contentWidth = w - (2 * padding);
      const availableHeight = h - (2 * padding);

      // Add title
      slide.addText(titleText, {
        x: x + padding,
        y: currentY,
        w: contentWidth,
        h: 0.2,
        fontSize: 10,
        fontFace: 'Arial',
        bold: true,
        color: '000000'
      });
      currentY += 0.25;

      // Add plain text with links (if any)
      if (parsed.segments.length > 0) {
        const textHeight = Math.min(0.8, availableHeight * 0.3);

        // Build text array with hyperlinks where applicable
        const textArray = [];
        parsed.segments.forEach(segment => {
          if (segment.isLink) {
            textArray.push({
              text: segment.text,
              options: {
                fontSize: 9,
                hyperlink: { url: segment.url, tooltip: segment.url },
                color: '0563C1',
                underline: true
              }
            });
          } else {
            textArray.push({
              text: segment.text,
              options: { fontSize: 9 }
            });
          }
        });

        slide.addText(textArray, {
          x: x + padding,
          y: currentY,
          w: contentWidth,
          h: textHeight,
          fontFace: 'Arial',
          color: '000000',
          valign: 'top'
        });
        currentY += textHeight + 0.1;
      }

      // Add tables
      tables.forEach((table, idx) => {
        const tableHeight = Math.min(
          table.rows.length * 0.25,
          y + h - currentY - padding - 0.1
        );

        if (tableHeight > 0.3) {
          slide.addTable(table.rows, {
            x: x + padding,
            y: currentY,
            w: contentWidth,
            h: tableHeight,
            fontSize: 9,
            border: { pt: 1, color: '999999' },
            fill: { color: 'F9F9F9' },
            autoPage: false,
            autoPageRepeatHeader: false
          });
          currentY += tableHeight + 0.1;
        }
      });

      // Add images with strict space checking
      images.forEach((image, idx) => {
        const remainingHeight = y + h - currentY - padding;

        // Only attempt to add image if we have at least 0.5 inches of space
        if (remainingHeight < 0.5) {
          console.warn(`Skipping image ${idx + 1}: insufficient space (${remainingHeight.toFixed(2)} inches remaining)`);
          return;
        }

        try {
          let imgWidth, imgHeight;

          // If we have natural dimensions, use them to maintain aspect ratio
          if (image.naturalWidth && image.naturalHeight) {
            const aspectRatio = image.naturalHeight / image.naturalWidth;

            // Convert pixels to inches (rough conversion: 96 DPI)
            const naturalWidthInches = image.naturalWidth / 96;
            const naturalHeightInches = image.naturalHeight / 96;

            // Start with natural size
            imgWidth = naturalWidthInches;
            imgHeight = naturalHeightInches;

            // Scale down if too wide
            if (imgWidth > contentWidth) {
              imgWidth = contentWidth;
              imgHeight = imgWidth * aspectRatio;
            }

            // Scale down if too tall - be conservative (leave 0.1 inch buffer)
            const maxHeight = remainingHeight - 0.1;
            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = imgHeight / aspectRatio;
            }
          } else {
            // Fallback: use available space if no natural dimensions
            imgWidth = Math.min(contentWidth, 2.0);
            imgHeight = Math.min(remainingHeight - 0.1, 1.5);
          }

          // Ensure minimum viable size
          if (imgWidth < 0.5) imgWidth = 0.5;
          if (imgHeight < 0.3) imgHeight = 0.3;

          // Final check: ensure image fits in remaining space
          if (imgHeight > remainingHeight - 0.05) {
            console.warn(`Skipping image ${idx + 1}: scaled height (${imgHeight.toFixed(2)}) exceeds remaining space (${remainingHeight.toFixed(2)})`);
            return;
          }

          slide.addImage({
            data: image.data,
            x: x + padding,
            y: currentY,
            w: imgWidth,
            h: imgHeight,
            sizing: { type: 'contain', w: imgWidth, h: imgHeight }
          });
          currentY += imgHeight + 0.1;
        } catch (error) {
          console.error('Error adding image:', error);
        }
      });
    };

    // Single slide with maximized content area
    const slide = pptx.addSlide();
    slide.background = { color: 'FFFFFF' };

    const topMargin = 0.15;
    const headerHeight = 0.85;
    const headerTextPadding = 0.08;
    const cornerRadius = 0.1; // Uniform corner radius for all boxes

    // Left header box - Report Info
    const leftHeaderX = leftMargin;
    const leftHeaderY = topMargin;

    const leftHeaderText = [
      { text: isKnowledgeGap ? 'Knowledge Gap: ' : 'Key Decision: ', options: { bold: true } },
      { text: (fields.title || 'Untitled') + '\n' },
      { text: 'Owner: ', options: { bold: true } },
      { text: (fields.owner || '') + '\n' },
      { text: isKnowledgeGap ? 'Contributors: ' : 'Decision Maker: ', options: { bold: true } },
      { text: (isKnowledgeGap ? (fields.contributors || '') : (fields.decision_maker || '')) + '\n' },
      { text: isKnowledgeGap ? 'Learning Cycle: ' : 'Integration Event: ', options: { bold: true } },
      { text: fields.learning_cycle || '' }
    ];

    slide.addText(leftHeaderText, {
      x: leftHeaderX, y: leftHeaderY, w: leftBoxWidth, h: headerHeight,
      fontSize: 10, fontFace: 'Arial',
      fill: { color: headerBg },
      line: { color: borderColor, width: 1 },
      shape: pptx.ShapeType.roundRect,
      rectRadius: cornerRadius,
      valign: 'top',
      margin: [headerTextPadding, headerTextPadding, headerTextPadding, headerTextPadding]
    });

    // Right header box - Project Info
    const rightHeaderX = leftHeaderX + leftBoxWidth + gap;

    const rightHeaderText = [
      { text: (fields.project_name || '') + '\n', options: { bold: true } },
      { text: (isKnowledgeGap ? `Knowledge Gap #${fields.kg_number || '______'}` : `Key Decision #${fields.kd_number || '______'}`) + '\n', options: { bold: true } },
      { text: 'Status: ', options: { bold: true } },
      { text: fields.status || 'draft' }
    ];

    slide.addText(rightHeaderText, {
      x: rightHeaderX, y: leftHeaderY, w: rightBoxWidth, h: headerHeight,
      fontSize: 10, fontFace: 'Arial',
      fill: { color: headerBg },
      line: { color: borderColor, width: 1 },
      shape: pptx.ShapeType.roundRect,
      rectRadius: cornerRadius,
      align: 'right',
      valign: 'top',
      margin: [headerTextPadding, headerTextPadding, headerTextPadding, headerTextPadding]
    });

      // Left column content boxes
      const contentStartY = leftHeaderY + headerHeight + 0.15;

      // The Question/Decision
      const box1Y = contentStartY;
      const box1H = 0.9;
      await addContentToBox(
        fields.description,
        leftHeaderX,
        box1Y,
        leftBoxWidth,
        box1H,
        isKnowledgeGap ? 'The Question to Answer: ' : 'The Key Decision: '
      );

      // The Purpose
      const box2Y = box1Y + box1H + 0.12;
      const box2H = 1.25;
      await addContentToBox(
        fields.purpose,
        leftHeaderX,
        box2Y,
        leftBoxWidth,
        box2H,
        'The Purpose: '
      );

      // What We Have Done
      const box3Y = box2Y + box2H + 0.12;
      const box3H = slideHeight - box3Y - topMargin;
      await addContentToBox(
        fields.what_we_have_done,
        leftHeaderX,
        box3Y,
        leftBoxWidth,
        box3H,
        isKnowledgeGap ? 'What We Will Do / What We Have Done: ' : 'What We Have Done: '
      );

      // Right column content boxes

      // What We Have Learned
      const rightBox1Y = contentStartY;
      const rightBox1H = isKnowledgeGap ? 3.4 : 2.75;
      await addContentToBox(
        fields.what_we_have_learned,
        rightHeaderX,
        rightBox1Y,
        rightBoxWidth,
        rightBox1H,
        'What We Have Learned: '
      );

      // Recommendations / Recommendations and Next Steps
      const rightBox2Y = rightBox1Y + rightBox1H + 0.12;
      const rightBox2H = slideHeight - rightBox2Y - topMargin;
      await addContentToBox(
        fields.recommendations,
        rightHeaderX,
        rightBox2Y,
        rightBoxWidth,
        rightBox2H,
        isKnowledgeGap ? 'Recommendations and Next Steps: ' : 'What We Recommend / What We Have Decided: '
      );

      // Generate filename and export
      const filename = generateExportFilename(formData, selectedReport, slideSize, 'pptx');

      await pptx.writeFile({ fileName: filename });
      message.destroy();
      message.success(`Exported: ${filename}`);
    } catch (err) {
      console.error('Export error:', err);
      message.destroy();
      message.error('Export failed. Please try again.');
    }
  };

  // Generate report from all voice transcripts - navigate to direct edit mode
  const handleGenerateReport = () => {
    message.success('Switching to direct edit mode to review your report...');
    // All content is already in formData from individual processing
    setInputMode('direct'); // Stay on step 2, switch to direct edit mode
  };

  // Splitter drag handlers
  const handleSplitterMouseDown = () => {
    setIsDragging(true);
  };

  const handleSplitterMouseMove = (e) => {
    if (!isDragging) return;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newRatio = Math.min(Math.max((x / rect.width) * 100, 30), 80); // Limit between 30% and 80%
    setSplitRatio(newRatio);
  };

  const handleSplitterMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse up listener
  React.useEffect(() => {
    if (isDragging) {
      const handleMouseUp = () => setIsDragging(false);
      document.addEventListener('mouseup', handleMouseUp);
      return () => document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [isDragging]);

  const exportPDF = async (layout = 'memo', slideSize = '16:9') => {
    if (!selectedReport) {
      message.warning('Please select a report type first.');
      return;
    }

    const fields = formData;
    const isKnowledgeGap = selectedReport === 'knowledge_gap';

    // Helper function to strip HTML tags
    const stripHTML = (html) => {
      if (!html) return '';
      return html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<p>/gi, '')
        .replace(/<\/li>/gi, '\n')
        .replace(/<li>/gi, '• ')
        .replace(/<ul>/gi, '')
        .replace(/<\/ul>/gi, '\n')
        .replace(/<ol>/gi, '')
        .replace(/<\/ol>/gi, '\n')
        .replace(/&nbsp;/g, ' ')
        .replace(/<[^>]*>/g, '')
        .trim();
    };

    if (layout === 'slide') {
      // Pipeline approach: Generate PPTX, send to backend for PDF conversion
      try {
        message.loading('Generating PowerPoint...', 0);

        // Generate PPTX using existing exportPowerPoint logic
        const pptxBlob = await generatePowerPointBlob(slideSize);

        message.destroy();
        message.loading('Converting to PDF...', 0);

        // Send PPTX to backend for conversion
        const formDataMultipart = new FormData();
        const pptxFilename = generateExportFilename(formData, selectedReport, slideSize, 'pptx');

        formDataMultipart.append('file', pptxBlob, pptxFilename);

        const response = await api.post('/report-ai/convert-pptx-to-pdf', formDataMultipart, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob'
        });

        message.destroy();

        // Download the PDF
        const pdfFilename = generateExportFilename(formData, selectedReport, slideSize, 'pdf');
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', pdfFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        message.success(`Exported: ${pdfFilename}`);
      } catch (error) {
        message.destroy();
        console.error('PDF conversion error:', error);
        message.error('PDF conversion failed. Please try exporting as PowerPoint instead.');
      }
      return;
    }

    // Memo layout - portrait A4, simple text format
    if (layout === 'memo') {
      try {
        message.loading('Generating PDF with images...', 0);

        const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const margin = 15;
      const contentWidth = pageWidth - (2 * margin);

      let yPosition = margin;

      // Add multi-line text with auto wrap
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 5) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * lineHeight);
      };

      // Header - Report Type and Title
      pdf.setFillColor(isKnowledgeGap ? 255 : 204, 255, isKnowledgeGap ? 102 : 102);
      pdf.rect(margin, yPosition, contentWidth, 20, 'F');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      yPosition += 8;
      pdf.text(isKnowledgeGap ? 'Knowledge Gap' : 'Key Decision', margin + 5, yPosition);
      pdf.setFontSize(14);
      yPosition += 8;
      pdf.text(fields.title || 'Untitled', margin + 5, yPosition);
      yPosition += 10;

      // Report Info Section
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.text('Owner:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(fields.owner || '', margin + 35, yPosition);
      yPosition += 5;

      pdf.setFont(undefined, 'bold');
      pdf.text(isKnowledgeGap ? 'Contributors:' : 'Decision Maker:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(isKnowledgeGap ? (fields.contributors || '') : (fields.decision_maker || ''), margin + 35, yPosition);
      yPosition += 5;

      pdf.setFont(undefined, 'bold');
      pdf.text('Learning Cycle:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(fields.learning_cycle || '', margin + 35, yPosition);
      yPosition += 5;

      // Project Info Section
      yPosition += 3;
      pdf.setFont(undefined, 'bold');
      pdf.text('Project:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(fields.project_name || '', margin + 35, yPosition);
      yPosition += 5;

      pdf.setFont(undefined, 'bold');
      pdf.text(isKnowledgeGap ? 'Knowledge Gap #:' : 'Key Decision #:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(isKnowledgeGap ? (fields.kg_number || '') : (fields.kd_number || ''), margin + 35, yPosition);
      yPosition += 5;

      pdf.setFont(undefined, 'bold');
      pdf.text('Status:', margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(fields.status || 'draft', margin + 35, yPosition);
      yPosition += 8;

      // Helper function to add tables to PDF as formatted text
      const addTableToPDF = (tableData) => {
        if (!tableData || tableData.rows.length === 0) return;

        // Calculate column widths based on content
        const colCount = tableData.colCount;
        const cellWidth = contentWidth / colCount;

        pdf.setFontSize(8);
        pdf.setFont(undefined, 'normal');

        tableData.rows.forEach((row, rowIndex) => {
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = margin;
          }

          let xPos = margin;

          row.forEach((cell, colIndex) => {
            // Draw cell border
            const cellHeight = 6;
            pdf.rect(xPos, yPosition - 4, cellWidth, cellHeight);

            // Add cell text
            if (cell.options && cell.options.bold) {
              pdf.setFont(undefined, 'bold');
            }
            const cellText = pdf.splitTextToSize(cell.text, cellWidth - 2);
            pdf.text(cellText[0] || '', xPos + 1, yPosition);
            if (cell.options && cell.options.bold) {
              pdf.setFont(undefined, 'normal');
            }

            xPos += cellWidth;
          });

          yPosition += 6;
        });

        yPosition += 3; // Space after table
      };

      // Content Sections with table and image support
      const addSection = async (title, content) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = margin;
        }

        // Add section title
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        yPosition = addWrappedText(title, margin, yPosition, contentWidth, 7) + 2;

        if (!content || content.trim() === '') {
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'italic');
          yPosition = addWrappedText('(No content)', margin, yPosition, contentWidth, 5) + 6;
          return;
        }

        // Extract tables, images, and links
        const tables = extractTablesFromHTML(content);
        const images = await extractImagesFromHTML(content);
        const parsed = parseHTMLWithLinks(content);

        // Add plain text with links - render segment by segment
        if (parsed.segments.length > 0) {
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'normal');

          // Render each segment with proper formatting
          let currentX = margin;

          parsed.segments.forEach(segment => {
            if (segment.text === '\n') {
              // New line
              yPosition += 5;
              currentX = margin;
              return;
            }

            // Split segment text if it wraps
            const segmentLines = pdf.splitTextToSize(segment.text, contentWidth - (currentX - margin));

            segmentLines.forEach((line, lineIdx) => {
              if (yPosition > 270) {
                pdf.addPage();
                yPosition = margin;
                currentX = margin;
              }

              const textWidth = pdf.getTextWidth(line);

              if (segment.isLink) {
                pdf.setTextColor(5, 99, 193);
                pdf.textWithLink(line, currentX, yPosition, { url: segment.url });
                pdf.setTextColor(0, 0, 0);
              } else {
                pdf.setTextColor(0, 0, 0);
                pdf.text(line, currentX, yPosition);
              }

              // If this segment wrapped to multiple lines, move to next line
              if (lineIdx < segmentLines.length - 1) {
                yPosition += 5;
                currentX = margin;
              } else {
                // Move X position for next segment on same line
                currentX += textWidth;

                // Check if we need to wrap to next line (approaching right margin)
                if (currentX > pageWidth - margin - 20) {
                  yPosition += 5;
                  currentX = margin;
                }
              }
            });
          });

          // Ensure we're on a new line after all segments
          if (currentX > margin) {
            yPosition += 5;
          }
          yPosition += 3;
        }

        // Add tables
        tables.forEach(table => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = margin;
          }
          addTableToPDF(table);
        });

        // Add images
        images.forEach(image => {
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = margin;
          }

          try {
            const maxWidth = contentWidth;
            const maxHeight = 80; // Max height in mm
            let imgWidth, imgHeight;

            // Use natural dimensions if available to maintain aspect ratio
            if (image.naturalWidth && image.naturalHeight) {
              const aspectRatio = image.naturalHeight / image.naturalWidth;

              // Convert pixels to mm (rough conversion: 96 DPI = 0.2646 mm/pixel)
              const naturalWidthMm = image.naturalWidth * 0.2646;
              const naturalHeightMm = image.naturalHeight * 0.2646;

              // Start with natural size
              imgWidth = naturalWidthMm;
              imgHeight = naturalHeightMm;

              // Scale down if too wide
              if (imgWidth > maxWidth) {
                imgWidth = maxWidth;
                imgHeight = imgWidth * aspectRatio;
              }

              // Scale down if too tall
              if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = imgHeight / aspectRatio;
              }
            } else {
              // Fallback: fit to available space
              imgWidth = maxWidth;
              imgHeight = maxHeight;
            }

            // Check if image fits on current page
            if (yPosition + imgHeight > 280) {
              pdf.addPage();
              yPosition = margin;
            }

            pdf.addImage(image.data, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 5;
          } catch (error) {
            console.error('Error adding image to PDF:', error);
            // Add placeholder text if image fails
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'italic');
            yPosition = addWrappedText(`[Image: ${image.alt}]`, margin, yPosition, contentWidth, 5) + 3;
          }
        });

        yPosition += 3; // Space after section
      };

        await addSection(
          isKnowledgeGap ? 'The Question to Answer:' : 'The Key Decision:',
          fields.description
        );

        await addSection('The Purpose:', fields.purpose);

        await addSection(
          isKnowledgeGap ? 'What We Will Do / What We Have Done:' : 'What We Have Done:',
          fields.what_we_have_done
        );

        await addSection('What We Have Learned:', fields.what_we_have_learned);

        await addSection(
          isKnowledgeGap ? 'Recommendations and Next Steps:' : 'What We Recommend / What We Have Decided:',
          fields.recommendations
        );

        const reportType = isKnowledgeGap ? 'KG' : 'KD';
        const reportNumber = isKnowledgeGap ? (fields.kg_number || '') : (fields.kd_number || '');
        const filename = `${reportType} ${reportNumber} ${fields.title || 'Report'} Memo.pdf`;

        pdf.save(filename);
        message.destroy();
        message.success(`Exported: ${filename}`);
      } catch (error) {
        console.error('PDF export error:', error);
        message.destroy();
        message.error('PDF export failed. Please try again.');
      }
    }
  };

  // Handler for Evaluate Against Best Practice
  const handleEvaluateReport = async () => {
    if (!selectedReport) {
      message.warning('Please select a report type first.');
      return;
    }

    // Open assistant panel if not already open
    if (!drawerOpen) {
      setDrawerOpen(true);
    }

    // Set active operation
    setActiveOperation('evaluate');

    addChatMessage({ role: 'user', content: "Evaluate this report against best practice." });
    setLoading(true);

    try {
      const reportData = formData;
      let response;

      if (selectedReport === 'knowledge_gap') {
        response = await reportAiService.evaluateKGReport(reportData);
      } else {
        response = await reportAiService.evaluateKDReport(reportData);
      }

      addChatMessage({
        role: 'ai',
        content: response.evaluation || response.error || "I couldn't evaluate the report."
      });
    } catch (err) {
      logger.error('Error evaluating report:', err);
      addChatMessage({
        role: 'ai',
        content: "Sorry, there was an error evaluating your report."
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler for Search Archived Reports
  const handleSearchArchive = async () => {
    if (!selectedReport) {
      message.warning('Please select a report type first.');
      return;
    }

    // Open assistant panel if not already open
    if (!drawerOpen) {
      setDrawerOpen(true);
    }

    // Set active operation
    setActiveOperation('archive');
    setLoading(true);

    // Add user message and loading message
    setChatMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: "Search archived reports for relevant information."
      },
      {
        role: 'ai',
        content: "Searching archive for relevant documents...",
        isLoading: true
      }
    ]);

    // Clear sources
    setSources([]);

    try {
      // Call the archive search API with current form data
      const result = await reportAiService.checkArchive(formData, selectedReport);

      // Remove loading message and add real response with embedded documents
      setChatMessages(prev => {
        const newMessages = [
          ...prev.filter(msg => !msg.isLoading),
          {
            role: 'ai',
            content: result.summary || "I couldn't find any relevant documents in our archive.",
            documents: result.documents || []  // Include documents for rendering clickable links
          }
        ];
        return newMessages;
      });

      // Update sources if we found any
      if (result.documents && result.documents.length > 0) {
        setSources(result.documents);
        message.success(`Found ${result.documents.length} relevant document(s)`);
      } else {
        message.info('No relevant documents found');
      }
    } catch (err) {
      logger.error('Error searching archive:', err);

      // Remove loading message and add error message
      setChatMessages(prev => [
        ...prev.filter(msg => !msg.isLoading),
        {
          role: 'ai',
          content: "Sorry, there was an error searching the archive. Please try again later."
        }
      ]);

      message.error('Failed to search archive');
    } finally {
      setLoading(false);
      setActiveOperation(null);
    }
  };

  const handleNewReport = () => {
    clearReport();
    form.resetFields();
    setSources([]);
    setCurrentStep(0);
    setInputMode(null);
    setSelectedReport('');
    setChatMessages([]);
    setVoiceTranscript('');
    setCurrentSection('');
    message.success('Session cleared');
  };

  const handleOpenDocument = async (projectId, documentId, filename) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const token = user?.access_token;

      if (!token) {
        message.error('Authentication token not found');
        return;
      }

      // For PowerPoint files, don't fetch the blob - just pass the document_id
      if (filename.toLowerCase().endsWith('.pptx') || filename.toLowerCase().endsWith('.ppt')) {
        setCurrentDocument({
          document_id: documentId,
          filename
        });
        setDocumentViewerOpen(true);
        return;
      }

      // For PDFs and other files, fetch the blob
      const response = await axios({
        url: `${API_URL}/archive/projects/${projectId}/documents/${documentId}/view`,
        method: 'GET',
        responseType: 'blob',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      // Open document in modal
      setCurrentDocument({
        url,
        filename,
        document_id: documentId
      });
      setDocumentViewerOpen(true);
    } catch (err) {
      logger.error('Error opening document:', err);
      message.error('Failed to open file');
    }
  };


  // Section navigation handlers
  const handlePreviousSection = () => {
    const currentIndex = voiceSections.findIndex(s => s.key === currentSection);
    if (currentIndex > 0) {
      setCurrentSection(voiceSections[currentIndex - 1].key);
    }
  };

  const handleNextSection = () => {
    const currentIndex = voiceSections.findIndex(s => s.key === currentSection);
    if (currentIndex < voiceSections.length - 1) {
      setCurrentSection(voiceSections[currentIndex + 1].key);
    }
  };

  // Helper function to open example PDF
  const handleOpenExample = async (pdfPath, e) => {
    e.stopPropagation(); // Prevent card click

    try {
      // Fetch PDF as blob to avoid localhost connection issues
      const response = await fetch(pdfPath);
      if (!response.ok) {
        throw new Error('Failed to load PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      setExamplePdfUrl(url);
      setExampleModalOpen(true);
    } catch (err) {
      logger.error('Error loading example PDF:', err);
      message.error('Unable to load example PDF. Please ensure the dev server is running.');
    }
  };




  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--rlc-surface-sunken)' }}>
      {/* Progress Steps */}
      <StepProgress currentStep={currentStep} />

      {/* Content */}
      {currentStep === 0 && (
        <>
          <ReportTypeSelection
            onSelectType={handleReportTypeSelect}
            onOpenExample={handleOpenExample}
          />
          <ExampleModal
            isOpen={exampleModalOpen}
            pdfUrl={examplePdfUrl}
            onClose={() => setExampleModalOpen(false)}
          />
        </>
      )}
      {currentStep === 1 && (
        <InputModeSelection
          selectedReport={selectedReport}
          onSelectMode={handleInputModeSelect}
          onBack={() => setCurrentStep(0)}
        />
      )}
      {currentStep === 2 && (
        <ReportFormFilling
          selectedReport={selectedReport}
          inputMode={inputMode}
          voiceSections={voiceSections}
          currentSection={currentSection}
          sectionTranscripts={sectionTranscripts}
          voiceTranscript={voiceTranscript}
          isRecording={isRecording}
          formData={formData}
          loading={loading}
          activeOperation={activeOperation}
          drawerOpen={drawerOpen}
          splitRatio={splitRatio}
          isDragging={isDragging}
          chatMessages={chatMessages}
          chatInput={chatInput}
          sources={sources}
          documentViewerOpen={documentViewerOpen}
          currentDocument={currentDocument}
          onSetCurrentStep={setCurrentStep}
          onSetInputMode={setInputMode}
          onGenerateReport={handleGenerateReport}
          onUpdateFormData={updateFormData}
          onSetCurrentSection={setCurrentSection}
          onStartVoiceInput={startVoiceInput}
          onHandleVoiceToAI={handleVoiceToAI}
          onSetSectionTranscripts={setSectionTranscripts}
          onSetVoiceTranscript={setVoiceTranscript}
          onPreviousSection={handlePreviousSection}
          onNextSection={handleNextSection}
          onEvaluateReport={handleEvaluateReport}
          onSearchArchive={handleSearchArchive}
          onValidateRequiredFields={validateRequiredFields}
          onSetDrawerOpen={setDrawerOpen}
          onSplitterMouseDown={handleSplitterMouseDown}
          onSplitterMouseMove={handleSplitterMouseMove}
          onSplitterMouseUp={handleSplitterMouseUp}
          onSetChatInput={setChatInput}
          onChatSubmit={handleChatSubmit}
          onOpenDocument={handleOpenDocument}
          onSetDocumentViewerOpen={setDocumentViewerOpen}
          onSetCurrentDocument={setCurrentDocument}
        />
      )}
      {currentStep === 3 && (
        <ReportActions
          onSetCurrentStep={setCurrentStep}
          onNewReport={handleNewReport}
          onExportPowerPoint={exportPowerPoint}
          onExportPDF={exportPDF}
        />
      )}
    </div>
  );
};

export default ReportWriterV2;
